import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Alert from "../../../components/common/Alert";
import Badge from "../../../components/common/Badge";
import Button from "../../../components/common/Button";
import Card from "../../../components/common/Card";
import Section from "../../../components/common/Section";
import { formatDateTime } from "../../../utils/reporting";
import {
  formatAmountWithImpact,
  formatSignedCurrency,
  formatStatusLabel,
  getStatusTone,
} from "../../../utils/cashingUpReporting";

const DetailPair = ({ label, value, emphasize = false }) => (
  <div className="grid grid-cols-[minmax(0,9rem)_minmax(0,1fr)] items-start gap-4 border-b border-slate-100 py-2.5 last:border-b-0">
    <p className="min-w-0 text-xs font-medium uppercase tracking-wide text-slate-500">
      {label}
    </p>
    <p
      className={[
        "min-w-0 break-words text-slate-900",
        emphasize ? "text-base font-semibold" : "text-sm font-medium",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {value || "-"}
    </p>
  </div>
);

const parseJsonValue = (value, fallback) => {
  if (value == null || value === "") return fallback;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const VarianceDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const session = location.state?.session || null;

  const varianceValue = Number(session?.cashVariance || 0);
  const varianceStyle = formatAmountWithImpact(varianceValue, session?.currencyCode);
  const storeLabel = session?.storeName || "-";
  const tillLabel = session?.tillName || "-";
  const closingCount = parseJsonValue(session?.closingCountJson, {});
  const cardVarianceValue = Number(
    closingCount.cardVariance ?? session?.cardVariance ?? 0,
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Variance</h1>
          <p className="text-sm text-slate-600">
            Read-only variance detail mirrored into Central.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            navigate({
              pathname: "/reporting/cashing-up/variances",
              search: location.search,
            })
          }
        >
          Back to Variances
        </Button>
      </div>

      {!session ? (
        <Alert tone="warning">
          Variance details are only available when you open this page from the
          Variances report.
        </Alert>
      ) : (
        <Card className="p-5 sm:p-6">
          <Section
            title="Variance Summary"
            subtitle="Expected versus actual cash discrepancy for this session."
          >
            <div className="grid gap-x-8 gap-y-4 md:grid-cols-2 xl:grid-cols-3">
              <DetailPair label="Store" value={storeLabel} />
              <DetailPair label="Till" value={tillLabel} />
              <DetailPair label="Business Date" value={session.businessDate || "-"} />
              <DetailPair
                label="Status"
                value={
                  <Badge tone={getStatusTone(session.status)}>
                    {formatStatusLabel(session.status)}
                  </Badge>
                }
              />
              <DetailPair label="Opened At" value={formatDateTime(session.openedAt)} />
              <DetailPair
                label="Opened By"
                value={session.openedBy || session.openedByUserId || "-"}
              />
              <DetailPair
                label="Closed By"
                value={session.closedBy || session.closedByUserId || "-"}
              />
              <DetailPair
                label="Closing Time"
                value={formatDateTime(session.closedAt)}
              />
              <DetailPair label="Expected Cash" value={formatSignedCurrency(session.expectedCash, session.currencyCode)} />
              <DetailPair label="Actual Cash" value={formatSignedCurrency(session.actualCash, session.currencyCode)} />
              <DetailPair
                label="Card Variance"
                value={formatSignedCurrency(cardVarianceValue, session.currencyCode)}
              />
              <DetailPair
                label="Cash Variance"
                value={
                  <span
                    className={
                      varianceStyle.tone === "rose"
                        ? "font-semibold text-rose-700"
                        : varianceStyle.tone === "emerald"
                          ? "font-semibold text-emerald-700"
                          : "font-semibold text-slate-700"
                    }
                  >
                    {varianceStyle.value}
                  </span>
                }
                emphasize
              />
            </div>
          </Section>
        </Card>
      )}
    </div>
  );
};

export default VarianceDetail;
